import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import ProgressBar from "@/components/ProgressBar";
import HeroSection from "@/components/HeroSection";
import TruthCard from "@/components/TruthCard";
import FooterSection from "@/components/FooterSection";
import TRUTHS from "@/data/truths";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentTruth, setCurrentTruth] = useState(0);
  const truthRefs = useRef([]);
  const hasTrackedView = useRef(false);

  // Track page view
  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    axios.post(`${API}/analytics/event`, {
      event_type: "page_view",
      metadata: { page: "landing" }
    }).catch(() => {});
  }, []);

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(Math.min(progress, 100));

      // Determine current truth based on scroll position
      const truthElements = truthRefs.current;
      for (let i = truthElements.length - 1; i >= 0; i--) {
        if (truthElements[i]) {
          const rect = truthElements[i].getBoundingClientRect();
          if (rect.top <= window.innerHeight / 2) {
            setCurrentTruth(i + 1);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const trackCtaClick = useCallback(() => {
    axios.post(`${API}/analytics/event`, {
      event_type: "cta_click",
      metadata: { button: "join_telegram" }
    }).catch(() => {});
  }, []);

  return (
    <div className="App min-h-screen" style={{ backgroundColor: "#0A0A0A" }}>
      <ProgressBar progress={scrollProgress} />

      {/* Current truth indicator */}
      {currentTruth > 0 && (
        <div
          data-testid="truth-counter"
          className="fixed top-4 right-4 z-50 px-3 py-1.5 rounded-full text-xs font-mono font-bold text-white border border-[#27272A]"
          style={{ backgroundColor: "rgba(10,10,10,0.85)", backdropFilter: "blur(8px)" }}
        >
          <span className="text-[#FF007F]">{currentTruth}</span>
          <span className="text-[#71717A]"> / 27</span>
        </div>
      )}

      <HeroSection onCtaClick={trackCtaClick} />

      {/* Cover slide */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <img
            src="/slide_01.png"
            alt="27 Brutal Truths - Cover"
            className="w-full rounded-2xl border border-[#27272A]"
            data-testid="cover-slide"
          />
        </div>
      </section>

      {/* Truth cards */}
      <section data-testid="truths-section" className="py-8 px-4 space-y-16 md:space-y-20">
        {TRUTHS.map((truth, index) => (
          <div
            key={truth.num}
            ref={(el) => (truthRefs.current[index] = el)}
          >
            <TruthCard truth={truth} index={index} />
          </div>
        ))}
      </section>

      <FooterSection onCtaClick={trackCtaClick} />
    </div>
  );
}

export default App;
